import type { ExchangeClient } from "@nktkas/hyperliquid";
import { useMemo } from "react";
import { arbitrum } from "viem/chains";
import {
	initExchangeClient,
	resetExchangeClient,
} from "#/features/trade/lib/hyperliquid";
import { signTypedData } from "#/lib/api/wallet";
import { useWallet } from "./use-wallet";

type CachedClient = {
	exchangeClient: ExchangeClient;
	address: `0x${string}`;
};

type CacheEntry = {
	address: string;
	promise: Promise<CachedClient>;
};

let cache: CacheEntry | null = null;

/**
 * Minimal wallet adapter matching the SDK's AbstractViemJsonRpcAccount. The
 * key never leaves the backend: every EIP-712 payload the ExchangeClient
 * builds is signed via `POST /v1/wallet/sign/typed-data` (rate-limited per
 * user), and the SDK broadcasts the signed action to Hyperliquid itself.
 */
function createBackendWallet(address: `0x${string}`) {
	return {
		signTypedData(params: {
			domain: Record<string, unknown>;
			types: Record<string, readonly { name: string; type: string }[]>;
			primaryType: string;
			message: Record<string, unknown>;
		}): Promise<`0x${string}`> {
			return signTypedData({
				domain: params.domain,
				types: {
					EIP712Domain: [
						{ name: "name", type: "string" },
						{ name: "version", type: "string" },
						{ name: "chainId", type: "uint256" },
						{ name: "verifyingContract", type: "address" },
					],
					...params.types,
				},
				primaryType: params.primaryType,
				message: params.message,
			}).then((result) => result.signature);
		},
		getAddresses(): Promise<`0x${string}`[]> {
			return Promise.resolve([address]);
		},
		getChainId(): Promise<number> {
			return Promise.resolve(arbitrum.id);
		},
	};
}

function createClient(address: `0x${string}`): Promise<CachedClient> {
	return Promise.resolve({
		exchangeClient: initExchangeClient(createBackendWallet(address)),
		address,
	});
}

export function useExchangeClient() {
	const { address } = useWallet();
	const currentAddress = address?.toLowerCase() ?? null;

	// Stable identity: a fresh {get, clear} per render made every effect that
	// lists the client in its deps re-fire on every render (the builder-fee /
	// referral side effects turned into endless sign+reject loops for unfunded
	// accounts). Only a signing-address change should produce a new client.
	return useMemo(() => {
		function get(): Promise<CachedClient> {
			// Invalidate the cached client if the signing address changed (login
			// as a different account). Otherwise the next mutation would sign with
			// the prior account's ExchangeClient.
			if (cache && cache.address !== currentAddress) {
				cache = null;
				resetExchangeClient();
			}
			if (!cache) {
				if (!currentAddress) {
					return Promise.reject(new Error("No wallet connected"));
				}
				cache = {
					address: currentAddress,
					promise: createClient(currentAddress as `0x${string}`),
				};
			}
			return cache.promise;
		}

		function clear() {
			cache = null;
			resetExchangeClient();
		}

		return { get, clear };
	}, [currentAddress]);
}
